// src/client/renderer/vfx/items/meteor-strike.ts
//
// Plan 17, Task 2.2 — Meteor Strike pool.
//
// Triggered by:
//   ItemCastEvent (itemId='meteor_strike') → phase='cast':
//     Meteor descends from sky (high-y) toward target position over 600ms
//     with fire-trail particles (single emitter mesh: trail line + meteor sprite).
//   ItemHitEvent (itemId='meteor_strike') → phase='impact':
//     Explosion sprite at target position (scale 0→2.0, opacity 1→0 over 400ms).
//
// Colour palette:
//   0xff6633 — fire orange-red (trail)
//   0xffaa33 — explosion core
//
// Pool: one-shot, max-active=3.
//
// GSAP: use tl.call(fn, undefined, absolutePos) — NOT eventCallback('onComplete').
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Impact explosions are
//   intentionally silent follow-up emitters.

import * as THREE from 'three';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import type { GameEvent, ItemCastEvent, ItemHitEvent } from '../../../../shared/game/types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_ACTIVE = 3;

/** Fire orange-red for the descending trail + meteor sprite. */
const TRAIL_COLOUR = 0xff6633;

/** Bright explosion core colour. */
const EXPLOSION_COLOUR = 0xffaa33;

/** Cast phase: meteor descends from sky over 600ms. */
const CAST_LIFETIME = 0.9;

/** Impact phase: explosion fades over 400ms. */
const IMPACT_LIFETIME = 0.65;

/** How high above the target the meteor starts. */
const SKY_OFFSET_Y = 12;

/** Explosion sprite final scale. */
const EXPLOSION_SCALE_END = 3.2;

/** Meteor body final scale. Mesh-based so the strike reads as an object, not a line. */
const METEOR_RADIUS = 0.46;

/** Thick trail radius. LineBasicMaterial width is not reliable in WebGL. */
const TRAIL_RADIUS = 0.16;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

function createTrailMesh(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
): THREE.Mesh {
  const start = new THREE.Vector3(from.x, from.y, from.z);
  const end = new THREE.Vector3(to.x, to.y, to.z);
  const delta = new THREE.Vector3().subVectors(end, start);
  const length = Math.max(delta.length(), 0.001);
  const geometry = new THREE.CylinderGeometry(
    TRAIL_RADIUS,
    TRAIL_RADIUS * 1.8,
    length,
    10,
    1,
    true,
  );
  const material = new THREE.MeshBasicMaterial({
    color: TRAIL_COLOUR,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(start).add(delta.multiplyScalar(0.5));
  mesh.quaternion.setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    end.clone().sub(start).normalize(),
  );
  return mesh;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createMeteorStrikePool
// ─────────────────────────────────────────────────────────────────────────────

export function createMeteorStrikePool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene, registry } = deps;
  const emitterStates = new Map<THREE.Object3D, EmitterState>();
  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;
    if (emitterStates.size >= MAX_ACTIVE) return null;

    const { phase, targetPosition } = args;

    // Fallback target if none provided
    const target = targetPosition ?? { x: 0, y: 0, z: 0 };

    const group = new THREE.Group();
    const handle = registry.create('meteor_strike');
    const tl = handle.timeline;
    const state: EmitterState = { handle, finished: false };

    if (phase === 'impact') {
      // ── Impact phase: explosion sprite at target ─────────────────────────
      const explosionMaterial = new THREE.SpriteMaterial({
        color: EXPLOSION_COLOUR,
        transparent: true,
        opacity: 1.0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const explosion = new THREE.Sprite(explosionMaterial);
      explosion.position.set(target.x, target.y, target.z);
      explosion.scale.set(0, 0, 1);
      group.add(explosion);

      const shockwaveMaterial = new THREE.MeshBasicMaterial({
        color: EXPLOSION_COLOUR,
        transparent: true,
        opacity: 0.75,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const shockwave = new THREE.Mesh(new THREE.RingGeometry(0.15, 0.28, 28), shockwaveMaterial);
      shockwave.position.set(target.x, target.y - 0.35, target.z);
      shockwave.rotation.x = Math.PI / 2;
      shockwave.scale.set(0.1, 0.1, 1);
      group.add(shockwave);

      // Scale 0 → EXPLOSION_SCALE_END over IMPACT_LIFETIME
      tl.to(
        explosion.scale,
        {
          x: EXPLOSION_SCALE_END,
          y: EXPLOSION_SCALE_END,
          z: 1,
          duration: IMPACT_LIFETIME,
          ease: 'power2.out',
        },
        0,
      );

      tl.to(
        shockwave.scale,
        {
          x: 5.5,
          y: 5.5,
          z: 1,
          duration: IMPACT_LIFETIME,
          ease: 'power2.out',
        },
        0,
      );

      // Opacity 1 → 0 over IMPACT_LIFETIME
      const opacityProxy = { value: 1.0 };
      tl.to(
        opacityProxy,
        {
          value: 0.0,
          duration: IMPACT_LIFETIME,
          ease: 'power2.in',
          onUpdate: () => {
            explosionMaterial.opacity = opacityProxy.value;
            shockwaveMaterial.opacity = opacityProxy.value * 0.75;
          },
        },
        0,
      );

      // Completion callback at IMPACT_LIFETIME seconds
      tl.call(
        () => {
          state.finished = true;
        },
        undefined,
        IMPACT_LIFETIME,
      );
    } else {
      // ── Cast phase: meteor descends from sky toward target ───────────────
      const skyStart = { x: target.x, y: target.y + SKY_OFFSET_Y, z: target.z };

      const trailMesh = createTrailMesh(skyStart, target);
      const trailMaterial = trailMesh.material as THREE.MeshBasicMaterial;
      group.add(trailMesh);

      // Meteor mesh starting at sky, descending to target.
      const meteorMaterial = new THREE.MeshBasicMaterial({
        color: TRAIL_COLOUR,
        transparent: true,
        opacity: 1.0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const meteor = new THREE.Mesh(
        new THREE.SphereGeometry(METEOR_RADIUS, 16, 12),
        meteorMaterial,
      );
      meteor.position.set(skyStart.x, skyStart.y, skyStart.z);
      meteor.scale.set(1, 1, 1);
      group.add(meteor);

      for (let index = 0; index < 8; index += 1) {
        const emberMaterial = new THREE.SpriteMaterial({
          color: index % 2 === 0 ? 0xffcc55 : TRAIL_COLOUR,
          transparent: true,
          opacity: 0.0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
        const ember = new THREE.Sprite(emberMaterial);
        ember.position.set(skyStart.x, skyStart.y + index * 0.12, skyStart.z);
        ember.scale.set(0.35, 0.35, 1);
        group.add(ember);
        const emberProxy = { value: 0.0 };
        tl.to(
          emberProxy,
          {
            value: 0.85,
            duration: CAST_LIFETIME * 0.25,
            ease: 'power1.out',
            yoyo: true,
            repeat: 1,
            delay: index * 0.035,
            onUpdate: () => {
              emberMaterial.opacity = emberProxy.value;
              ember.position.set(
                meteor.position.x + Math.sin(index * 1.7) * 0.18,
                meteor.position.y + 0.75 + index * 0.13,
                meteor.position.z + Math.cos(index * 1.7) * 0.18,
              );
            },
          },
          0,
        );
      }

      // Trail fades in over first half of cast lifetime
      const trailOpacityProxy = { value: 0.0 };
      tl.to(
        trailOpacityProxy,
        {
          value: 0.8,
          duration: CAST_LIFETIME * 0.5,
          ease: 'power1.in',
          onUpdate: () => {
            trailMaterial.opacity = trailOpacityProxy.value;
          },
        },
        0,
      );

      // Meteor descends from skyStart → target over CAST_LIFETIME
      const posProxy = { y: skyStart.y };
      tl.to(
        posProxy,
        {
          y: target.y,
          duration: CAST_LIFETIME,
          ease: 'power2.in',
          onUpdate: () => {
            meteor.position.y = posProxy.y;
          },
        },
        0,
      );

      tl.to(
        meteor.rotation,
        {
          x: Math.PI * 2.4,
          y: Math.PI * 1.7,
          duration: CAST_LIFETIME,
          ease: 'none',
        },
        0,
      );

      // Completion callback at CAST_LIFETIME seconds
      tl.call(
        () => {
          state.finished = true;
        },
        undefined,
        CAST_LIFETIME,
      );
    }

    scene.add(group);
    emitterStates.set(group, state);
    return group;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.
  // The shared registry is ticked by the caller (item-vfx-manager at Task 6.1)
  // exactly once per frame across all pools.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true;
    return state.finished;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return;
    state.handle.dispose();
    emitterStates.delete(emitter);
    scene.remove(emitter);
    disposeSkillshotGroup(emitter);
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const [emitter, state] of emitterStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    emitterStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveMeteorStrikeForEvent
// ─────────────────────────────────────────────────────────────────────────────

export function resolveMeteorStrikeForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'meteor_strike'; args: ItemVfxSpawnArgs } | null {
  // Only handle item_cast and item_hit for meteor_strike
  if (event.type !== 'item_cast' && event.type !== 'item_hit') {
    return null;
  }

  if (event.itemId !== 'meteor_strike') return null;

  let casterId: string;
  let targetId: string | undefined;

  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    casterId = cast.casterId;
    targetId = cast.targetId;
  } else {
    // item_hit
    const hit = event as ItemHitEvent;
    casterId = hit.casterId;
    targetId = hit.targetId;
  }

  const casterPosition =
    casterId && getPositionForPlayer ? getPositionForPlayer(casterId) : undefined;
  const targetPosition =
    targetId && getPositionForPlayer ? getPositionForPlayer(targetId) : undefined;

  const phase: ItemVfxSpawnArgs['phase'] = event.type === 'item_cast' ? 'cast' : 'impact';

  return {
    poolId: 'meteor_strike',
    args: {
      phase,
      casterPosition,
      targetPosition,
    },
  };
}
